import { ModelUtils } from "./ModelUtils";


export class PlaneMeshCreator {
    get vertexBuffer(): Float32Array { return this._vertexBuf; }
    private _vertexBuf: Float32Array;

    get indexBuffer(): Uint16Array { return this._indexBuf; }
    private _indexBuf: Uint16Array;

    get uvBuffer(): Float32Array { return this._uvBuf; }
    private _uvBuf: Float32Array;

    private _normals: number[];
    private _hasUVs: boolean;

    get planeCnt(): number { return this._planeCnt; }
    private _planeCnt: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._planeCnt = 0;
        this._hasUVs = false;
        this._normals = undefined;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    addPlane(pos: pc.Vec3, vertexOffsets: pc.Vec4, uvs?: pc.Vec4): void {
        if (this._planeCnt == 0) {
            this._hasUVs = uvs != undefined;
        }
        else if ((uvs != undefined) != this._hasUVs) {
            throw new Error("PlaneMeshCreator:addPlane: " + this._hasUVs ? "All planes must have UVs" : "No plane may have UVs");
        }

        // Zajistit kapacitu bufferu
        this.ensureBuffersCapacity(this._planeCnt + 1);

        // Vrcholy plane
        let i = this._planeCnt * 4 * 3;

        this._vertexBuf[i++] = pos.x + vertexOffsets.x;
        this._vertexBuf[i++] = pos.y + vertexOffsets.y;
        this._vertexBuf[i++] = pos.z;

        this._vertexBuf[i++] = pos.x + vertexOffsets.z;
        this._vertexBuf[i++] = pos.y + vertexOffsets.y;
        this._vertexBuf[i++] = pos.z;

        this._vertexBuf[i++] = pos.x + vertexOffsets.z;
        this._vertexBuf[i++] = pos.y + vertexOffsets.w;
        this._vertexBuf[i++] = pos.z;

        this._vertexBuf[i++] = pos.x + vertexOffsets.x;
        this._vertexBuf[i++] = pos.y + vertexOffsets.w;
        this._vertexBuf[i] = pos.z;

        // Indexy vrcholu pro trojuhelniky           
        let vertexId = this._planeCnt * 4;
        i = this._planeCnt * 6;

        this._indexBuf[i++] = vertexId;
        this._indexBuf[i++] = vertexId + 1;
        this._indexBuf[i++] = vertexId + 2;
        this._indexBuf[i++] = vertexId;
        this._indexBuf[i++] = vertexId + 2;
        this._indexBuf[i] = vertexId + 3;

        // UV pro vrcholy
        if (uvs) {
            i = this._planeCnt * 8;

            this._uvBuf[i++] = uvs.x;
            this._uvBuf[i++] = 1 - uvs.y;

            this._uvBuf[i++] = uvs.x + uvs.z;
            this._uvBuf[i++] = 1 - uvs.y;

            this._uvBuf[i++] = uvs.x + uvs.z;
            this._uvBuf[i++] = 1 - (uvs.y + uvs.w);

            this._uvBuf[i++] = uvs.x;
            this._uvBuf[i] = 1 - (uvs.y + uvs.w);
        }

        this._planeCnt++;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    calculateNormals(): void {
        this._normals = ModelUtils.calculateNormals(this._vertexBuf, this._indexBuf, this._planeCnt * 4);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    initMesh(mesh: pc.Mesh): pc.Mesh {
        mesh.setPositions(this._vertexBuf, 3, this._planeCnt * 4);
        mesh.setIndices(this._indexBuf, this._planeCnt * 6);

        if (this._hasUVs)
            mesh.setUvs(0, this._uvBuf, 2, this._planeCnt * 4);

        if (this._normals)
            mesh.setNormals(this._normals);

        mesh.update();

        return mesh;
    }

    //-------------------------------------------------------------------------
    /**
     * Zajistit dostatecnou kapacitu bufferu
     * @param planeCnt Pocet planes, pro ktere potrebujeme kapacitu
     */
    //-------------------------------------------------------------------------
    private ensureBuffersCapacity(planeCnt: number): void {
        // Buffer pro pozice vrcholu
        let i = planeCnt * 4 * 3;

        if (!this._vertexBuf || this._vertexBuf.length < i) {
            // Vertex buffer
            let vertexBuf = new Float32Array(i);

            if (this._vertexBuf)
                vertexBuf.set(this._vertexBuf);

            this._vertexBuf = vertexBuf;

            // Index buffer
            let indexBuf = new Uint16Array(planeCnt * 6);

            if (this._indexBuf)
                indexBuf.set(this._indexBuf);

            this._indexBuf = indexBuf;

            if (this._hasUVs) {
                let uvBuf = new Float32Array(planeCnt * 8);

                if (this._uvBuf)
                    uvBuf.set(this._uvBuf);

                this._uvBuf = uvBuf;
            }
        }
    }
}



export class PlaneMesh {
    private static readonly VERTEX_CNT = 4;

    //-------------------------------------------------------------------------
    /**
     * Vytvoreni meshe pro plane. Plane je kolma (pozice vrcholu na ose Z je 0). Poradi vrcholu je [dolni levy okraj, dolni pravy okraj, horni pravy okraj, horni levy okraj]
     * @param gd 
     * @param vertices Vrcholy plane (x = levy okraj, y = dolni okraj, z = pravy okraj, w = horni okraj)
     * @param uvs 
     * @param color 
     * @returns 
     */
    //-------------------------------------------------------------------------
    static createMesh(gd: pc.GraphicsDevice, vertices: pc.Vec4, uvs?: pc.Vec4, color?: pc.Color | pc.Color[]): pc.Mesh {
        // Vytvorit mesh
        let mesh = new pc.Mesh(gd);

        // Nastavit vrcholy
        let positions = new Float32Array([
            // bottom left
            vertices.x, vertices.y, 0,
            // bottom right
            vertices.z, vertices.y, 0,
            // top right
            vertices.z, vertices.w, 0,
            // top left
            vertices.x, vertices.w, 0
        ]);

        let indices = new Uint32Array([0, 1, 2, 0, 2, 3]);

        mesh.setPositions(positions);
        mesh.setIndices(indices);
        mesh.setNormals(new Float32Array(pc.calculateNormals(positions, indices)));

        // UVs
        if (uvs) {
            let uvsArray = new Float32Array([
                uvs.x, 1 - uvs.y,
                uvs.x + uvs.z, 1 - uvs.y,
                uvs.x + uvs.z, 1 - (uvs.y + uvs.w),
                uvs.x, 1 - (uvs.y + uvs.w)
                /*0, 1,
                1, 1,
                1, 0,
                0, 0*/
                /*uvs.x, uvs.y + uvs.w,
                uvs.x + uvs.z, uvs.y + uvs.w,
                uvs.x + uvs.z, uvs.y,
                uvs.x, uvs.y*/
            ]);

            mesh.setUvs(0, uvsArray);
        }

        // Barvy
        if (color) {
            let colors = new Uint8Array(4 * PlaneMesh.VERTEX_CNT);
            let i = 0;

            if (!Array.isArray(color)) {
                let r = color.r * 255;
                let g = color.g * 255;
                let b = color.b * 255;
                let a = color.a * 255;

                for (let vertexId = 0; vertexId < PlaneMesh.VERTEX_CNT; vertexId++) {
                    colors[i++] = r;
                    colors[i++] = g;
                    colors[i++] = b;
                    colors[i++] = a;
                }
            }
            else {
                for (let vertexId = 0; vertexId < PlaneMesh.VERTEX_CNT; vertexId++) {
                    let col = color[vertexId];

                    colors[i++] = col.r * 255;
                    colors[i++] = col.g * 255;
                    colors[i++] = col.b * 255;
                    colors[i++] = col.a * 255;
                }
            }
        }

        mesh.update();
        return mesh;
    }
}